import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Scene3D from './Scene3D';

export default function LegalPage() {
  const { i18n } = useTranslation();
  const isFr = i18n.language === 'fr';

  return (
    <div className="min-h-screen text-zinc-950 relative">
      <Scene3D />
      <div className="relative z-10">
      {/* Header */}
      <div className="bg-zinc-50/70 backdrop-blur-md border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            {isFr ? "Retour à l'accueil" : "Back to home"}
          </Link>
          <h1 className="font-outfit font-bold text-3xl md:text-4xl">
            {isFr ? "Mentions Légales" : "Legal Notice"}
          </h1>
          <p className="font-manrope text-sm text-zinc-500 mt-2">
            {isFr ? "Dernière mise à jour : Février 2025" : "Last updated: February 2025"}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="space-y-8">
          
          {/* Section 1 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="soft-card p-6 md:p-8"
          >
            <h2 className="font-outfit font-semibold text-lg mb-4">
              {isFr ? "1. Éditeur du site" : "1. Site Publisher"}
            </h2>
            <div className="font-manrope text-sm text-zinc-600 leading-relaxed space-y-3">
              <p><strong className="text-zinc-950">Med Stud Tech</strong></p>
              <p>{isFr ? "Société par actions simplifiée (SAS)" : "Simplified Joint Stock Company (SAS)"}</p>
              <p>{isFr ? "Capital social : 1 000 €" : "Share capital: €1,000"}</p>
              <p className="pt-2"><strong className="text-zinc-950">{isFr ? "Siège social :" : "Registered office:"}</strong></p>
              <p>20 rue des Balkans<br />75020 Paris, France</p>
              <p className="pt-2"><strong className="text-zinc-950">{isFr ? "Immatriculation :" : "Registration:"}</strong></p>
              <p>SIREN : 924 618 275<br />SIRET : 924 618 275 00011<br />RCS Paris</p>
            </div>
          </motion.div>

          {/* Section 2 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="soft-card p-6 md:p-8"
          >
            <h2 className="font-outfit font-semibold text-lg mb-4">
              {isFr ? "2. Hébergement" : "2. Hosting"}
            </h2>
            <div className="font-manrope text-sm text-zinc-600 leading-relaxed space-y-2">
              <p><strong className="text-zinc-950">Hostinger International Ltd.</strong></p>
              <p>61 Lordou Vironos Street, 6023 Larnaca, Cyprus</p>
              <p>Site web : https://www.hostinger.fr</p>
            </div>
          </motion.div>

          {/* Section 3 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="soft-card p-6 md:p-8"
          >
            <h2 className="font-outfit font-semibold text-lg mb-4">
              {isFr ? "3. Propriété intellectuelle" : "3. Intellectual Property"}
            </h2>
            <div className="font-manrope text-sm text-zinc-600 leading-relaxed space-y-3">
              <p>
                {isFr 
                  ? "L'ensemble du contenu de ce site (textes, images, graphismes, logo, icônes, logiciels, etc.) est la propriété exclusive de Med Stud Tech ou de ses partenaires et est protégé par les lois françaises et internationales relatives à la propriété intellectuelle."
                  : "All content on this site (texts, images, graphics, logos, icons, software, etc.) is the exclusive property of Med Stud Tech or its partners and is protected by French and international intellectual property laws."
                }
              </p>
              <p>
                {isFr 
                  ? "Toute reproduction, représentation, modification, publication ou transmission, totale ou partielle, du site ou de son contenu est interdite sans l'autorisation écrite préalable de Med Stud Tech."
                  : "Any reproduction, representation, modification, publication, or transmission, in whole or in part, of the site or its content is prohibited without prior written authorization from Med Stud Tech."
                }
              </p>
            </div>
          </motion.div>

          {/* Section 4 - RGPD */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="soft-card p-6 md:p-8"
          >
            <h2 className="font-outfit font-semibold text-lg mb-4">
              {isFr ? "4. Protection des données personnelles (RGPD)" : "4. Personal Data Protection (GDPR)"}
            </h2>
            <div className="font-manrope text-sm text-zinc-600 leading-relaxed space-y-3">
              <p>
                {isFr 
                  ? "Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez des droits suivants :"
                  : "In accordance with the General Data Protection Regulation (GDPR), you have the following rights:"
                }
              </p>
              <ul className="space-y-2 pl-4">
                <li><span className="text-sky-500 mr-2">•</span> {isFr ? "Droit d'accès à vos données" : "Right to access your data"}</li>
                <li><span className="text-sky-500 mr-2">•</span> {isFr ? "Droit de rectification" : "Right to rectification"}</li>
                <li><span className="text-sky-500 mr-2">•</span> {isFr ? "Droit à l'effacement" : "Right to erasure"}</li>
                <li><span className="text-sky-500 mr-2">•</span> {isFr ? "Droit à la portabilité" : "Right to portability"}</li>
                <li><span className="text-sky-500 mr-2">•</span> {isFr ? "Droit d'opposition" : "Right to object"}</li>
              </ul>
              <p className="pt-2">
                <strong className="text-zinc-950">{isFr ? "Données collectées :" : "Data collected:"}</strong><br />
                {isFr 
                  ? "Le formulaire de contact collecte : nom, email, sujet et message. Ces données sont utilisées uniquement pour répondre à vos demandes."
                  : "The contact form collects: name, email, subject, and message. This data is used solely to respond to your requests."
                }
              </p>
              <p>
                <strong className="text-zinc-950">{isFr ? "Durée de conservation :" : "Retention period:"}</strong> {isFr ? "3 ans" : "3 years"}
              </p>
              <p>
                <strong className="text-zinc-950">{isFr ? "Contact CNIL :" : "CNIL Contact:"}</strong> www.cnil.fr
              </p>
            </div>
          </motion.div>

          {/* Section 5 - Cookies */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="soft-card p-6 md:p-8"
          >
            <h2 className="font-outfit font-semibold text-lg mb-4">
              5. Cookies
            </h2>
            <div className="font-manrope text-sm text-zinc-600 leading-relaxed space-y-3">
              <p>
                {isFr 
                  ? "Ce site utilise uniquement des cookies techniques essentiels au fonctionnement :"
                  : "This site uses only essential technical cookies:"
                }
              </p>
              <ul className="space-y-2 pl-4">
                <li><span className="text-sky-500 mr-2">•</span> {isFr ? "Cookies de session" : "Session cookies"}</li>
                <li><span className="text-sky-500 mr-2">•</span> {isFr ? "Cookies de préférence linguistique (FR/EN)" : "Language preference cookies (FR/EN)"}</li>
              </ul>
              <p>
                {isFr 
                  ? "Aucun cookie publicitaire ou de tracking n'est utilisé."
                  : "No advertising or tracking cookies are used."
                }
              </p>
            </div>
          </motion.div>

          {/* Section 6 - Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="soft-card p-6 md:p-8"
          >
            <h2 className="font-outfit font-semibold text-lg mb-4">
              6. Contact
            </h2>
            <div className="font-manrope text-sm text-zinc-600 leading-relaxed space-y-2">
              <p><strong className="text-zinc-950">Med Stud Tech</strong></p>
              <p>20 rue des Balkans<br />75020 Paris, France</p>
              <p className="pt-2">
                {isFr 
                  ? "Ou via le formulaire de contact disponible sur le site."
                  : "Or via the contact form available on the site."
                }
              </p>
            </div>
          </motion.div>

          {/* Section 7 - Droit applicable */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="soft-card p-6 md:p-8"
          >
            <h2 className="font-outfit font-semibold text-lg mb-4">
              {isFr ? "7. Droit applicable" : "7. Applicable Law"}
            </h2>
            <div className="font-manrope text-sm text-zinc-600 leading-relaxed">
              <p>
                {isFr 
                  ? "Les présentes mentions légales sont soumises au droit français. En cas de litige, compétence est attribuée aux tribunaux français."
                  : "These legal notices are subject to French law. In case of dispute, jurisdiction is granted to French courts."
                }
              </p>
            </div>
          </motion.div>

        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-zinc-200 text-center">
          <p className="font-manrope text-sm text-zinc-500">
            © {new Date().getFullYear()} Med Stud Tech. {isFr ? "Tous droits réservés." : "All rights reserved."}
          </p>
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 mt-4 text-sm text-sky-600 hover:text-sky-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {isFr ? "Retour à l'accueil" : "Back to home"}
          </Link>
        </div>
      </div>
      </div>
    </div>
  );
}
